import ButtonGroup from 'devextreme-react/button-group';
import DateBox from 'devextreme-react/date-box';
import { useMemo, type CSSProperties } from 'react';
import {
  applyDurationMode,
  applyDurationModeToDraft,
  DURATION_MODE_ITEMS,
  inferDurationMode,
  type TimeOffDurationMode
} from '../timeOffDuration';

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ops-color-muted, #475569)',
  marginBottom: 8
};

export interface TimeOffDurationDraft {
  startLocal: Date;
  endLocal: Date;
  isAllDay: boolean;
}

interface TimeOffDurationFieldsProps<T extends TimeOffDurationDraft> {
  draft: T;
  onDraftChange: (updater: (prev: T) => T) => void;
  disabled?: boolean;
  disabledDates?: Date[];
}

export function TimeOffDurationFields<T extends TimeOffDurationDraft>({
  draft,
  onDraftChange,
  disabled = false,
  disabledDates
}: TimeOffDurationFieldsProps<T>) {
  const mode = inferDurationMode(draft.startLocal, draft.endLocal, draft.isAllDay);
  const singleDateMode = mode !== 'custom';
  // Stable reference so DevExtreme ButtonGroup does not treat every parent
  // re-render as a selection change (which otherwise re-fires
  // onSelectionChanged and drives an infinite update loop — ITB-1633).
  const selectedItemKeys = useMemo(() => [mode], [mode]);

  function setMode(nextMode: TimeOffDurationMode) {
    onDraftChange((prev) => applyDurationModeToDraft(prev, nextMode));
  }

  function setSingleDate(value: Date) {
    onDraftChange((prev) => {
      const currentMode = inferDurationMode(prev.startLocal, prev.endLocal, prev.isAllDay);
      return applyDurationModeToDraft(prev, currentMode, value);
    });
  }

  return (
    <div className="time-off-duration-fields">
      <div>
        <div style={labelStyle}>Duration</div>
        <ButtonGroup
          items={DURATION_MODE_ITEMS}
          keyExpr="key"
          selectedItemKeys={selectedItemKeys}
          stylingMode="outlined"
          selectionMode="single"
          disabled={disabled}
          onSelectionChanged={(e) => {
            const key = e.addedItems[0]?.key;
            // Only react to a genuine change. DevExtreme re-fires this event
            // when the controlled selection syncs on re-render; calling setMode
            // for the already-selected key would loop indefinitely (ITB-1633).
            if (typeof key === 'string' && key !== mode) setMode(key as TimeOffDurationMode);
          }}
        />
      </div>

      {singleDateMode ? (
        <DateBox
          key="single"
          type="date"
          value={draft.startLocal}
          disabledDates={disabledDates}
          onValueChanged={(e) => {
            const v = e.value;
            if (!(v instanceof Date) || Number.isNaN(v.getTime())) return;
            setSingleDate(v);
          }}
          stylingMode="outlined"
          label={mode === 'allDay' ? 'Day' : 'Date'}
          labelMode="outside"
          disabled={disabled}
          width="100%"
        />
      ) : (
        <div className="time-off-duration-fields__dates">
          <DateBox
            key="custom-start"
            type="datetime"
            value={draft.startLocal}
            disabledDates={disabledDates}
            onValueChanged={(e) => {
              const v = e.value;
              if (!(v instanceof Date) || Number.isNaN(v.getTime())) return;
              onDraftChange((prev) => ({ ...prev, startLocal: v }));
            }}
            stylingMode="outlined"
            label="Start"
            labelMode="outside"
            disabled={disabled}
            width="100%"
          />
          <DateBox
            key="custom-end"
            type="datetime"
            value={draft.endLocal}
            disabledDates={disabledDates}
            onValueChanged={(e) => {
              const v = e.value;
              if (!(v instanceof Date) || Number.isNaN(v.getTime())) return;
              onDraftChange((prev) => ({ ...prev, endLocal: v }));
            }}
            stylingMode="outlined"
            label="End"
            labelMode="outside"
            disabled={disabled}
            width="100%"
          />
        </div>
      )}

      {mode === 'morning' || mode === 'afternoon' ? (
        <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)' }}>
          {mode === 'morning'
            ? 'Morning half-day: 9:00 AM – 1:00 PM'
            : 'Afternoon half-day: 1:00 PM – 5:00 PM'}
        </div>
      ) : null}
    </div>
  );
}

/** Build draft times from a duration mode and calendar date anchor. */
export function durationTimesFromMode(mode: TimeOffDurationMode, baseDate: Date) {
  return applyDurationMode(mode, baseDate);
}
